import { PatientsList, PatientsTopBarActions, type PtContactFilter } from '@elva/ui';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { usePageTopBar } from '@/app/layouts/pageTopBar';
import { ElvaPageLoader } from '@/components/ElvaPageLoader';
import { adaptPatientRoster } from '../adapters/patientAdapters';
import { usePatientSearch } from '../hooks/usePatientSearch';
import { patientProfilePath } from '../utils/patientProfileRoutes';

const SEARCH_DEBOUNCE_MS = 300;
const CONTACT_FILTERS: PtContactFilter[] = ['texts-off', 'stopped-topic'];

export function PatientsListPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');

  useEffect(() => {
    const trimmed = query.trim();
    if (!trimmed) {
      setDebouncedQuery('');
      return;
    }
    const id = window.setTimeout(() => {
      setDebouncedQuery(trimmed);
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      window.clearTimeout(id);
    };
  }, [query]);

  const isSearching = debouncedQuery.length > 0;
  const { data: rosterData, isLoading, isError } = usePatientSearch();
  const { data: searchData, isFetching: isSearchLoading } = usePatientSearch(
    isSearching ? debouncedQuery : undefined,
  );

  const { today, tomorrow } = useMemo(() => adaptPatientRoster(rosterData), [rosterData]);
  const searchResults = useMemo(
    () => (isSearching ? adaptPatientRoster(searchData).today : []),
    [isSearching, searchData],
  );

  if (isError) console.error('Failed to load patient roster');

  usePageTopBar({
    actions: <PatientsTopBarActions />,
  });

  const contactParam = params.get('contact');
  const contactFilter =
    contactParam !== null && CONTACT_FILTERS.includes(contactParam as PtContactFilter)
      ? (contactParam as PtContactFilter)
      : undefined;

  return (
    <div className="pp-root v6-root relative flex min-h-0 flex-1 flex-col overflow-hidden">
      <div className="pp-glow" aria-hidden />
      {isLoading && today.length === 0 && tomorrow.length === 0 ? (
        <ElvaPageLoader />
      ) : (
        <PatientsList
          today={today}
          tomorrow={tomorrow}
          searchQuery={query}
          onSearchQueryChange={setQuery}
          searchResults={searchResults}
          isSearchLoading={isSearching && (isSearchLoading || query.trim() !== debouncedQuery)}
          onSelectPatient={(id) => { void navigate(patientProfilePath(id)); }}
          contactFilter={contactFilter}
          onContactFilter={(f) => { if (f === null) setParams({}); else setParams({ contact: f }); }}
        />
      )}
    </div>
  );
}
